import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from "react";

export type ModalKind = "info" | "warning" | "danger";
export type ModalAction = {
  label: string;
  value: string;
  variant?: "primary" | "secondary" | "danger";
};
export type ModalRequest = {
  kind?: ModalKind;
  title: string;
  message: string;
  actions: ModalAction[];
  destructive?: boolean;
};

type QueuedModal = ModalRequest & {
  id: number;
  resolve: (value: string) => void;
};

type ModalContextValue = {
  open: (request: ModalRequest) => Promise<string>;
};

const ModalContext = createContext<ModalContextValue | null>(null);
let nextModalId = 1;

export function ModalProvider({ children }: PropsWithChildren) {
  const [current, setCurrent] = useState<QueuedModal | null>(null);
  const queueRef = useRef<QueuedModal[]>([]);

  const showNext = useCallback(() => {
    setCurrent((active) => active || queueRef.current.shift() || null);
  }, []);

  const open = useCallback((request: ModalRequest) => {
    return new Promise<string>((resolve) => {
      const item: QueuedModal = { ...request, id: nextModalId++, resolve };
      queueRef.current.push(item);
      showNext();
    });
  }, [showNext]);

  const closeWith = useCallback((value: string) => {
    setCurrent((active) => {
      if (active) active.resolve(value);
      return null;
    });
    requestAnimationFrame(showNext);
  }, [showNext]);

  useEffect(() => {
    if (!current) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      const cancel = current.actions.find((action) => action.value === "cancel") || current.actions[0];
      if (cancel) closeWith(cancel.value);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [current, closeWith]);

  const value = useMemo(() => ({ open }), [open]);

  return (
    <ModalContext.Provider value={value}>
      {children}
      {current ? (
        <div
          className="modal-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target !== event.currentTarget) return;
            if (current.destructive || current.kind === "danger") return;
            const cancel = current.actions.find((action) => action.value === "cancel") || current.actions[0];
            if (cancel) closeWith(cancel.value);
          }}
        >
          <section className={`app-modal modal-${current.kind || "info"}`} role="dialog" aria-modal="true">
            <header className="app-modal-head">
              <h2>{current.title}</h2>
            </header>
            <p className="app-modal-message">{current.message}</p>
            <footer className="app-modal-actions">
              {current.actions.map((action) => (
                <button
                  key={action.value}
                  className={`btn btn-${action.variant || "secondary"}`}
                  type="button"
                  onClick={() => closeWith(action.value)}
                >
                  {action.label}
                </button>
              ))}
            </footer>
          </section>
        </div>
      ) : null}
    </ModalContext.Provider>
  );
}

export function useModal() {
  const ctx = useContext(ModalContext);
  if (!ctx) throw new Error("useModal must be used inside ModalProvider");
  return ctx;
}
